// utils/callUtils.js
import React from 'react';
import { Modal, Button, Space, Typography } from 'antd';

const { Text } = Typography;

/**
 * Handle phone call with direct call option
 * @param {string} phoneNumber - The phone number to call (digits and "+" only)
 * @param {boolean} showDirectCall - Whether to offer direct call (default: true)
 * @param {Record<string,string>} customOptions - Additional { name: uri } pairs
 * @returns {Array<{name:string,uri:string,icon:React.ReactNode}>}
 */
export function handlePhoneContact(
  phoneNumber,
  { showDirectCall = true, customOptions = {} } = {}
) {
  const clean = phoneNumber.replace(/[^\d+]/g, '');
  const opts = [];

  if (showDirectCall) {
    opts.push({ name: 'Phone Call', uri: `tel:${clean}`, icon: '📞' });
  }
  Object.entries(customOptions).forEach(([name, uri]) => {
    opts.push({ name, uri, icon: '📱' });
  });
  return opts;
}

/**
 * Show an AntD Modal with call options (pure JSX, uses Modal.info under the hood)
 * @param {string} phoneNumber
 * @param {string} contactName
 * @param {{showDirectCall?,customOptions?}} opts
 */
export function showCallOptionsModal(
  phoneNumber,
  contactName = '',
  opts = {}
) {
  const options = handlePhoneContact(phoneNumber, opts);
  if (options.length === 0) return;

  let chosenUri = null;

  // If there's only one option, go directly to it
  if (options.length === 1) {
    window.open(options[0].uri, '_self');
    return;
  }
  
  // Otherwise show a modal with options
  Modal.info({
    title: `Contact ${contactName || phoneNumber}`,
    icon: null,
    okText: 'Cancel',
    width: 300,
    onOk: () => {
      if (chosenUri) {
        const target = chosenUri.startsWith('tel:') ? '_self' : '_blank';
        window.open(chosenUri, target);
      }
    },
    content: (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Text>Choose how you'd like to call:</Text>
        {options.map(({ name, uri, icon }, i) => (
          <Button
            key={i}
            block
            icon={<span style={{ marginRight: 8 }}>{icon}</span>}
            onClick={() => {
              chosenUri = uri;
              Modal.destroyAll();
            }}
          >
            {name}
          </Button>
        ))}
      </Space>
    ),
  });
}
